"use client";

import type { ReactNode } from "react";
import Link from "@/lib/spa/Link";
import { useHash, usePathname, useSearchParams } from "@/lib/spa/navigation";
import { useSymbolStore } from "@/lib/stores/symbolStore";
import {
  currentReturnHref,
  withStockReturn,
} from "@/lib/stock/briefHref";
import { applyPathSymbol } from "@/lib/stock/briefHandoff";
import { stampStockReturn } from "@/lib/stock/briefReturn";
import { heatBg, heatFg, fmtPct } from "@/components/timing/topDown/TopDownContext";

export function Card({
  title,
  right,
  children,
}: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line bg-surface p-2.5 shadow-panel">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-muted">{title}</h4>
        {right}
      </div>
      {children}
    </section>
  );
}

export function StockLogo({ symbol, size = 28 }: { symbol: string; size?: number }) {
  return (
    <img
      src={`/api/logo/${encodeURIComponent(symbol)}`}
      alt=""
      loading="lazy"
      width={size}
      height={size}
      className="shrink-0 rounded bg-white object-contain ring-1 ring-line"
      style={{ width: size, height: size }}
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

export function ChangeChip({ label, v }: { label: string; v: number | null }) {
  return (
    <span
      className="rounded px-1 py-0.5 text-2xs font-semibold tabular-nums"
      style={{ background: heatBg(v), color: heatFg(v) }}
      title={`${label} change`}
    >
      {label} {fmtPct(v, 2)}%
    </span>
  );
}

const MORE_CLASS = "text-2xs font-semibold text-accent hover:underline";

export function MoreLink({ href, children }: { href: string; children: ReactNode }) {
  const pathname = usePathname();
  const hash = useHash();
  const search = useSearchParams();
  const symbol = useSymbolStore((s) => s.symbol);
  const from = currentReturnHref(pathname, search, hash);
  const dest = withStockReturn(href, from, symbol);
  return (
    <Link
      href={dest}
      className={MORE_CLASS}
      onClick={() => {
        stampStockReturn({ href: from, symbol });
        applyPathSymbol(href, symbol);
      }}
    >
      {children}
    </Link>
  );
}

export function MoreButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={MORE_CLASS}>
      {children}
    </button>
  );
}

const BACK_CLASS =
  "rounded-md px-2.5 py-1 text-[11px] font-semibold text-accent ring-1 ring-line hover:bg-accent-soft";

export function BriefBack({
  onBack,
  href,
  onClick,
  children,
}: {
  onBack?: () => void;
  href?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (onBack) {
    return (
      <button type="button" onClick={onBack} className={BACK_CLASS}>
        {children}
      </button>
    );
  }
  return (
    <Link href={href ?? "/"} className={BACK_CLASS} onClick={onClick}>
      {children}
    </Link>
  );
}

export function RatingBadge({ rating }: { rating: "BUY" | "HOLD" | "SELL" }) {
  const p = {
    BUY: "bg-bull-soft text-bull ring-bull/30",
    HOLD: "bg-warn-soft text-warn ring-warn/30",
    SELL: "bg-bear-soft text-bear ring-bear/30",
  }[rating];
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ring-1 ${p}`}>
      {rating}
    </span>
  );
}
